import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import * as lodash from 'lodash';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type { IDistributionCenter } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import type {
	BlueprintDistributionCenterLink,
	GetDistributionCentersByDssiCodesArgs,
} from '../ogm-hub';
import {
	BlueprintControllerV3,
	BlueprintDistributionCenterController,
	LocationDistributionCenterController,
} from '../ogm-hub';

export const distributionCentersAdapter =
	createEntityAdapter<IDistributionCenter>();

export const fetchDistributionCentersForBlueprint = createAsyncThunk<
	IDistributionCenter[],
	number,
	{ rejectValue: string[] | Response }
>(
	'distributionCenters/fetchDistributionCentersForBlueprint',
	async (blueprintId: number, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				BlueprintControllerV3.GetDistributionCenterInfo(blueprintId)
			);

			if (result.errors.length == 0) {
				const distributionCenters: IDistributionCenter[] = result.results.map(
					(dc) => {
						return {
							id: dc.id,
							name: dc.name,
							dssiCode: dc.dssiCode,
							externalCode: dc.externalCode,
							supplierId: dc.supplierId,
						};
					}
				);
				return distributionCenters;
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface fetchDssiCodesForCustomerAndSupplierArgs {
	customerId: number;
	supplierId: number;
}

export const fetchDssiCodesForCustomerAndSupplier = createAsyncThunk<
	string[],
	fetchDssiCodesForCustomerAndSupplierArgs,
	{ rejectValue: string[] | Response }
>(
	'distributionCenters/fetchDssiCodesForCustomerAndSupplier',
	async (
		args: fetchDssiCodesForCustomerAndSupplierArgs,
		{ rejectWithValue }
	) => {
		try {
			const result = await unwrapFetch2(
				LocationDistributionCenterController.GetCustomerSupplierDistributionCenterDssiCodes(
					args.customerId,
					args.supplierId
				)
			);

			return result.distributionCenterDssiCodes;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchDistributionCentersForDssiCodes = createAsyncThunk<
	IDistributionCenter[],
	GetDistributionCentersByDssiCodesArgs,
	{ rejectValue: string[] | Response }
>(
	'distributionCenters/fetchDistributionCentersForDssiCodes',
	async (
		args: GetDistributionCentersByDssiCodesArgs,
		{ rejectWithValue, getState }
	) => {
		try {
			const state = getState();

			const cachedDcs = selectDistributionCentersByDssiCodes(
				state,
				args.distributionCenterDssiCodes
			).filter((item) => item !== undefined && item !== null);
			const missingDssiCodes = lodash.difference(
				args.distributionCenterDssiCodes,
				cachedDcs.map((item) => item.dssiCode)
			);

			if (missingDssiCodes.length === 0) {
				return cachedDcs;
			}

			const result: any = await unwrapFetch2(
				LocationDistributionCenterController.GetDistributionCentersByDssiCodes({
					distributionCenterDssiCodes: missingDssiCodes,
				})
			);
			const distributionCenters: IDistributionCenter[] =
				result.distributionCenters.map((dc) => {
					return {
						id: dc.id,
						name: dc.name,
						dssiCode: dc.dssiCode,
						externalCode: dc.externalCode,
						supplierId: dc.supplierId,
					};
				});

			return cachedDcs.concat(distributionCenters);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface FetchBlueprintDcLinksForCustomerAndSuppliersArgs {
	customerId: number;
	supplierIds: number[];
}

export const fetchBlueprintDcLinksForCustomerAndSupplier = createAsyncThunk<
	BlueprintDistributionCenterLink[],
	FetchBlueprintDcLinksForCustomerAndSuppliersArgs,
	{ rejectValue: string[] | Response }
>(
	'distributionCenters/fetchBlueprintDcLinksForCustomerAndSupplier',
	async (
		args: FetchBlueprintDcLinksForCustomerAndSuppliersArgs,
		{ rejectWithValue }
	) => {
		try {
			const result = await unwrapFetch2(
				BlueprintDistributionCenterController.GetBlueprintDistributionCenterLinksForCustomerAndMultipleSuppliers(
					args
				)
			);

			return result.blueprintDistributionCenterLinks;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export type IDistributionCentersState = EntityState<IDistributionCenter>;

export const distributionCentersSlice = createSlice({
	name: 'distributionCenters',
	initialState: distributionCentersAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(
			fetchDistributionCentersForBlueprint.fulfilled,
			(state, action) => {
				distributionCentersAdapter.upsertMany(state, action.payload);
			}
		);
		builder.addCase(
			fetchDistributionCentersForDssiCodes.fulfilled,
			(state, action) => {
				distributionCentersAdapter.upsertMany(state, action.payload);
			}
		);
	},
});

export function selectDistributionCenterByDssiCode(
	state: IAppState,
	code: string
) {
	for (const key in state.distributionCenters?.entities) {
		if (state.distributionCenters.entities[key].dssiCode === code) {
			return state.distributionCenters.entities[key];
		}
	}
	return null;
}

export function selectDistributionCentersByDssiCodes(
	state,
	codes: string[]
): IDistributionCenter[] {
	return codes.map((code) => selectDistributionCenterByDssiCode(state, code));
}
